import { useCallback, useMemo, useState } from 'react'
import { isTextLed, type ImageStageLook } from './imageStage'
import { readImageStageLook, writeImageStageLook } from './imageStagePreference'

export interface ImageStageLayout {
  /** Whether the words lead this note. Decided when the note opens, never while typing, so the layout does not move under the caret. */
  textLed: boolean
  /** How the stage shows: the person's choice on this device, else large for an image-led note and compact for a text-led one. */
  look: ImageStageLook
  setLook: (look: ImageStageLook) => void
}

/**
 * `openKey` changes whenever the note is opened again (leaving and returning), so a note that grew while it was open is weighed afresh
 * on the next open and not before.
 */
export function useImageStageLayout(noteId: string, openKey: unknown, body: string): ImageStageLayout {
  // The body is read once per opening of the note, on purpose: the weight never changes while it is being typed.
  const textLed = useMemo(() => isTextLed(body), [noteId, openKey])
  const [version, setVersion] = useState(0)
  // `version` marks a write to the stored choice.
  const stored = useMemo(() => readImageStageLook(noteId), [noteId, version])
  const setLook = useCallback((look: ImageStageLook) => {
    writeImageStageLook(noteId, look)
    setVersion((current) => current + 1)
  }, [noteId])
  return { textLed, look: stored ?? (textLed ? 'compact' : 'large'), setLook }
}
